import { useState, useEffect, useCallback, useRef } from "react"
import {
  Activity, Cpu, Users, Layers, Search, RefreshCw,
  FileText, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle, Settings, Shield, TrendingUp, Zap,
  MessageCircle, Clock, Trash2, RotateCcw, Play,
  Server, Terminal, Send, X, Power, Eye, EyeOff,
  BookOpen, ChevronUp, Check, XCircle, Target,
  Map,
} from "lucide-react"
import Markdown from "react-markdown"
import {
  getStatusSnapshot, getMetricsHistory, getFeed,
  generateShiftReport, getDiagnosis,
  getQueue, clearQueue, getServices, restartService, testApi,
  getAutopilotStatus, toggleAutopilot, getAuthorityStatus, vetoAction,
  getSlos, getRunbooks, executeRunbook,
  acknowledgeAlert, resolveAlert,
  type StatusSnapshot, type MetricsPoint, type FeedEntry,
  type QueueJob, type QueueData, type ServiceInfo, type ApiTestResult,
  type AutopilotStatus, type AuthorityStatus, type PendingAction,
  type SloData, type SloStatus, type RunbookInfo,
} from "@/lib/api"
import { timeAgo } from "@/lib/utils"
import { useSystemStore } from "@/store/systemStore"
import NetworkTopology from "@/components/NetworkTopology"
import DiagnosisPanel from "@/components/DiagnosisPanel"
import NodeDetailDrawer from "@/components/NodeDetailDrawer"

// ── Pulse Card (top stat) ────────────────────────────────────────────────

function PulseCard({ value, label, sub, color }: {
  value: string | number
  label: string
  sub?: string
  color?: string
}) {
  return (
    <div className="flex-1 min-w-0 rounded-xl bg-card/60 border border-border/40 p-3 text-center">
      <div
        className="text-2xl font-mono font-bold leading-none"
        style={{ color: color || "#e2e8f0" }}
      >
        {value}
      </div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1.5">{label}</div>
      {sub && <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>}
    </div>
  )
}

// ── Feed entry ───────────────────────────────────────────────────────────

const FEED_ICONS: Record<string, { icon: typeof CheckCircle; color: string }> = {
  job_success: { icon: CheckCircle, color: "#22c55e" },
  job_failed: { icon: AlertTriangle, color: "#ef4444" },
  alert_critical: { icon: AlertTriangle, color: "#ef4444" },
  alert_warn: { icon: AlertTriangle, color: "#f59e0b" },
  alert_info: { icon: Activity, color: "#3b82f6" },
  autopilot: { icon: Settings, color: "#8b5cf6" },
  slo_met: { icon: Shield, color: "#22c55e" },
  slo_miss: { icon: Shield, color: "#ef4444" },
}

// ── Sparkline ────────────────────────────────────────────────────────────

function MiniSparkline({ data, color, label, current, unit }: {
  data: (number | null)[]
  color: string
  label: string
  current: string | number
  unit?: string
}) {
  const filtered = data.map((v, i) => v !== null ? { i, v } : null).filter(Boolean) as { i: number; v: number }[]
  const h = 28
  const w = 120

  let pathD = ""
  if (filtered.length >= 2) {
    const vals = filtered.map(p => p.v)
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    const range = max - min || 1
    pathD = filtered
      .map((p, idx) => {
        const x = (p.i / (data.length - 1)) * w
        const y = h - ((p.v - min) / range) * (h - 4) - 2
        return `${idx === 0 ? "M" : "L"}${x},${y}`
      })
      .join(" ")
  }

  return (
    <div className="flex items-center gap-2 bg-card/30 border border-border/30 rounded-lg px-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm font-mono font-bold mt-0.5" style={{ color }}>
          {current}{unit || ""}
        </div>
      </div>
      {pathD && (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-20 shrink-0" style={{ height: h }}>
          <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  )
}

// ── Section toggle ───────────────────────────────────────────────────────

function Section({ title, icon: Icon, children, defaultOpen, badge }: {
  title: string
  icon: typeof Activity
  children: React.ReactNode
  defaultOpen?: boolean
  badge?: string
}) {
  const [open, setOpen] = useState(defaultOpen ?? true)
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center w-full px-4 py-2.5 gap-2.5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <Icon size={13} className="text-gray-500 shrink-0" />
        <span className="text-[11px] text-gray-400 uppercase tracking-wider font-medium flex-1">{title}</span>
        {badge && <span className="text-[10px] text-gray-600 font-mono">{badge}</span>}
        {open ? <ChevronDown size={12} className="text-gray-600" /> : <ChevronRight size={12} className="text-gray-600" />}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  )
}

// ── Autopilot Row ─────────────────────────────────────────────────────────

function AutopilotRow({ autopilot, authority, onToggle, onVeto }: {
  autopilot: AutopilotStatus | null
  authority: AuthorityStatus | null
  onToggle: () => void
  onVeto: (id: string) => void
}) {
  if (!autopilot) return null
  const pending = authority?.pending_actions ?? []

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card/40 border border-border/30">
      {/* Toggle */}
      <button
        onClick={onToggle}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
          autopilot.enabled
            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
            : "bg-gray-500/15 text-gray-400 border border-gray-500/30"
        }`}
      >
        <Power size={11} />
        Autopilot {autopilot.enabled ? "ON" : "OFF"}
      </button>

      <span className="text-[10px] text-gray-600 font-mono">
        {autopilot.actions_taken} actions
      </span>

      <div className="flex-1" />

      {/* Pending veto count */}
      {pending.length > 0 && (
        <div className="flex items-center gap-1">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[10px] font-medium border border-amber-500/25 animate-pulse">
            <Eye size={10} />
            {pending.length} pending
          </span>
        </div>
      )}
    </div>
  )
}

// ── Pending Actions (Veto Panel) ──────────────────────────────────────────

function VetoPanel({ actions, onVeto }: { actions: PendingAction[]; onVeto: (id: string) => void }) {
  if (actions.length === 0) return null

  return (
    <div className="space-y-1.5">
      {actions.map(a => {
        const pct = Math.max(0, Math.min(100, (a.veto_remaining_seconds / 300) * 100))
        return (
          <div key={a.action_id} className="flex items-start gap-2 py-2 px-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs">
            <div className="flex-1 min-w-0">
              <div className="text-gray-200 font-medium">{a.action}</div>
              <div className="text-gray-500 text-[10px] mt-0.5">{a.reason}</div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-[10px] text-gray-600 font-mono shrink-0">
                  {Math.ceil(a.veto_remaining_seconds)}s
                </span>
              </div>
            </div>
            <button
              onClick={() => onVeto(a.action_id)}
              className="px-2 py-1 rounded bg-red-500/15 hover:bg-red-500/25 text-red-400 text-[10px] font-medium border border-red-500/25 shrink-0"
            >
              Veto
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ── SLO Row ───────────────────────────────────────────────────────────────

function SloRow({ slos }: { slos: SloData | null }) {
  const [expandedSlo, setExpandedSlo] = useState<string | null>(null)

  if (!slos || Object.keys(slos.slos).length === 0) return null

  const entries = Object.values(slos.slos)
  const severityColor: Record<string, string> = {
    ok: "#22c55e",
    watch: "#f59e0b",
    investigate: "#f97316",
    emergency: "#ef4444",
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-1.5 overflow-x-auto pb-0.5">
        {entries.map(slo => (
          <button
            key={slo.slo_name}
            onClick={() => setExpandedSlo(expandedSlo === slo.slo_name ? null : slo.slo_name)}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-card/40 border border-border/30 shrink-0 hover:bg-card/60 active:bg-card/80 transition-colors text-left"
          >
            <div
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${slo.severity !== "ok" ? "animate-pulse" : ""}`}
              style={{ backgroundColor: severityColor[slo.severity] || "#6b7280" }}
            />
            <span className="text-[10px] text-gray-400 truncate max-w-20">{slo.slo_name.replace(/_/g, " ")}</span>
            <span
              className="text-[10px] font-mono font-bold"
              style={{ color: severityColor[slo.severity] || "#6b7280" }}
            >
              {slo.burn_rate.toFixed(1)}x
            </span>
            <span className="text-[9px] text-gray-600">
              {Math.round(slo.budget_remaining * 100)}%
            </span>
          </button>
        ))}
      </div>
      {/* Expanded SLO detail */}
      {expandedSlo && slos.slos[expandedSlo] && (() => {
        const slo = slos.slos[expandedSlo]
        const color = severityColor[slo.severity] || "#6b7280"
        return (
          <div className="rounded-lg bg-card/50 border border-border/30 px-3 py-2 space-y-1.5 animate-in slide-in-from-top-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-300">{slo.slo_name.replace(/_/g, " ")}</span>
              <button onClick={() => setExpandedSlo(null)} className="text-gray-600 hover:text-gray-400">
                <X size={12} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-sm font-mono font-bold" style={{ color }}>{slo.burn_rate.toFixed(1)}x</div>
                <div className="text-[9px] text-gray-500 uppercase">Burn Rate</div>
              </div>
              <div>
                <div className="text-sm font-mono font-bold" style={{ color }}>{Math.round(slo.budget_remaining * 100)}%</div>
                <div className="text-[9px] text-gray-500 uppercase">Budget Left</div>
              </div>
              <div>
                <div className="text-sm font-mono font-bold" style={{ color: slo.met ? "#22c55e" : "#ef4444" }}>{slo.met ? "Met" : "Missed"}</div>
                <div className="text-[9px] text-gray-500 uppercase">Status</div>
              </div>
            </div>
            {slo.message && (
              <div className="text-[10px] text-gray-400 leading-relaxed">{slo.message}</div>
            )}
          </div>
        )
      })()}
    </div>
  )
}

// ── Interactive Feed Item ─────────────────────────────────────────────────

function InteractiveFeedItem({ entry, onAck, onResolve }: {
  entry: FeedEntry
  onAck?: (id: number) => void
  onResolve?: (id: number) => void
}) {
  const cfg = FEED_ICONS[entry.icon] || { icon: Activity, color: "#6b7280" }
  const Icon = cfg.icon
  const ts = entry.ts ? new Date(entry.ts) : null
  const timeStr = ts ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""
  const isAlert = entry.type === "alert" && entry.id != null

  return (
    <div className="flex items-start gap-3 py-2.5 px-1 group">
      <span className="text-[11px] text-gray-600 font-mono w-12 shrink-0 pt-0.5 text-right">
        {timeStr}
      </span>
      <Icon size={14} className="shrink-0 mt-0.5" style={{ color: cfg.color }} />
      <span className="text-sm text-gray-300 leading-snug flex-1 min-w-0">
        {entry.message}
      </span>
      {/* Action buttons for alerts */}
      {isAlert && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {onAck && (
            <button
              onClick={() => onAck(entry.id!)}
              className="p-1 rounded hover:bg-amber-500/20 text-gray-600 hover:text-amber-400 transition-colors"
              title="Acknowledge"
            >
              <Check size={11} />
            </button>
          )}
          {onResolve && (
            <button
              onClick={() => onResolve(entry.id!)}
              className="p-1 rounded hover:bg-emerald-500/20 text-gray-600 hover:text-emerald-400 transition-colors"
              title="Resolve"
            >
              <CheckCircle size={11} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Runbooks Panel ────────────────────────────────────────────────────────

function RunbooksPanel() {
  const [runbooks, setRunbooks] = useState<RunbookInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [executing, setExecuting] = useState<string | null>(null)
  const [message, setMessage] = useState("")
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    getRunbooks().then(d => setRunbooks(d.runbooks)).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const handleExecute = async (rb: RunbookInfo) => {
    if (!confirm(`Execute "${rb.name}"?\n\nThis is a ${rb.danger} ${rb.authority_label} action.`)) return
    setExecuting(rb.id)
    try {
      const result = await executeRunbook(rb.id)
      setMessage(result.success ? `Executed: ${rb.name}` : `Failed: ${result.error || result.detail}`)
      // Reload to get updated can_execute / last_executed
      const d = await getRunbooks()
      setRunbooks(d.runbooks)
    } catch (e: any) {
      setMessage(`Error: ${e.message}`)
    }
    setExecuting(null)
    setTimeout(() => setMessage(""), 5000)
  }

  if (loading) return <div className="text-center py-4"><RefreshCw size={14} className="text-gray-600 animate-spin mx-auto" /></div>

  const dangerColor: Record<string, string> = {
    safe: "text-emerald-400 bg-emerald-500/15 border-emerald-500/25",
    caution: "text-amber-400 bg-amber-500/15 border-amber-500/25",
    dangerous: "text-red-400 bg-red-500/15 border-red-500/25",
  }

  return (
    <div className="space-y-2">
      {message && <div className="text-xs text-accent">{message}</div>}
      <div className="space-y-1.5">
        {runbooks.map(rb => (
          <div key={rb.id} className="rounded-lg bg-card/30 border border-border/20 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 py-2 px-2.5 text-xs">
              <button
                onClick={() => setExpanded(expanded === rb.id ? null : rb.id)}
                className="text-gray-600 hover:text-gray-400"
              >
                {expanded === rb.id ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              </button>
              <BookOpen size={11} className="text-gray-500 shrink-0" />
              <span className="text-gray-200 flex-1 truncate">{rb.name}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] border ${dangerColor[rb.danger] || dangerColor.caution}`}>
                {rb.danger}
              </span>
              <span className="text-[10px] text-gray-600">T{rb.authority_tier}</span>
              <button
                onClick={() => handleExecute(rb)}
                disabled={!rb.can_execute || executing === rb.id}
                className="px-2 py-1 rounded bg-accent/15 hover:bg-accent/25 text-accent text-[10px] font-medium border border-accent/25 disabled:opacity-40"
                title={rb.can_execute ? "Execute" : "On cooldown"}
              >
                {executing === rb.id ? <RefreshCw size={10} className="animate-spin" /> : <Play size={10} />}
              </button>
            </div>
            {/* Expanded details */}
            {expanded === rb.id && (
              <div className="px-3 pb-2.5 space-y-2 border-t border-border/20 pt-2">
                <div className="text-[11px] text-gray-400">{rb.description}</div>
                <div className="text-[10px] text-gray-600">
                  <span className="text-gray-500">Trigger:</span> {rb.trigger}
                </div>
                <div className="text-[10px] text-gray-600">
                  <span className="text-gray-500">Steps:</span>
                  <ol className="ml-3 mt-0.5 space-y-0.5 list-decimal">
                    {rb.steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                </div>
                {rb.last_executed && (
                  <div className="text-[10px] text-gray-600">
                    Last run: {new Date(rb.last_executed * 1000).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Ops Panel (Queue, Services, API Test, Runbooks) ───────────────────────

function OpsPanel() {
  const [tab, setTab] = useState<"queue" | "services" | "runbooks" | "api">("queue")
  const tabs = [
    { key: "queue" as const, label: "Queue" },
    { key: "services" as const, label: "Services" },
    { key: "runbooks" as const, label: "Runbooks" },
    { key: "api" as const, label: "API Test" },
  ]
  return (
    <div className="space-y-3">
      {/* Tab selector */}
      <div className="flex gap-1 bg-card/40 rounded-lg p-0.5">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
              tab === t.key ? "bg-white/10 text-white" : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "queue" && <QueuePanel />}
      {tab === "services" && <ServicesPanel />}
      {tab === "runbooks" && <RunbooksPanel />}
      {tab === "api" && <ApiTestPanel />}
    </div>
  )
}

function QueuePanel() {
  const [data, setData] = useState<QueueData | null>(null)
  const [loading, setLoading] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [userFilter, setUserFilter] = useState("")
  const [message, setMessage] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const d = await getQueue(userFilter ? { user: userFilter } : undefined)
      setData(d)
    } catch { }
    setLoading(false)
  }, [userFilter])

  useEffect(() => { load() }, [load])

  const handleClear = async (mode: "all" | "user" | "job", target?: string) => {
    const label = mode === "all" ? "Clear ALL queued jobs?" : mode === "user" ? `Clear jobs for ${target}?` : `Cancel job ${target?.slice(0, 8)}...?`
    if (!confirm(label)) return
    setClearing(true)
    try {
      const result = await clearQueue(mode, target)
      setMessage(`Cleared ${result.cleared} job${result.cleared !== 1 ? "s" : ""}`)
      await load()
    } catch (e: any) {
      setMessage(`Error: ${e.message}`)
    }
    setClearing(false)
    setTimeout(() => setMessage(""), 3000)
  }

  return (
    <div className="space-y-3">
      {/* Stats */}
      {data && (
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-400">
            <span className="font-mono text-white">{data.stats.total}</span> queued
          </span>
          {data.stats.publisher > 0 && <span className="text-gray-500">pub: {data.stats.publisher}</span>}
          {data.stats.priority > 0 && <span className="text-gray-500">pri: {data.stats.priority}</span>}
          {data.stats.default > 0 && <span className="text-gray-500">def: {data.stats.default}</span>}
          <div className="flex-1" />
          <button onClick={load} disabled={loading} className="text-gray-500 hover:text-gray-300">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      )}

      {/* User filter + clear all */}
      <div className="flex gap-2">
        <input
          value={userFilter}
          onChange={e => setUserFilter(e.target.value)}
          placeholder="Filter by user ID..."
          className="flex-1 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-accent/50"
        />
        {userFilter && (
          <button
            onClick={() => handleClear("user", userFilter)}
            disabled={clearing}
            className="px-2.5 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs border border-red-500/20 disabled:opacity-50"
          >
            Clear User
          </button>
        )}
        <button
          onClick={() => handleClear("all")}
          disabled={clearing || (data?.stats.total ?? 0) === 0}
          className="px-2.5 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs border border-red-500/20 disabled:opacity-50"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {/* Message */}
      {message && <div className="text-xs text-accent">{message}</div>}

      {/* Job list */}
      {data && data.jobs.length > 0 ? (
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {data.jobs.map(job => (
            <div key={job.id} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-card/30 border border-border/20 text-xs">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                job.status === "processing" ? "bg-blue-500 animate-pulse" :
                job.status === "queued" ? "bg-gray-500" :
                job.status === "failed" ? "bg-red-500" : "bg-green-500"
              }`} />
              <span className="font-mono text-gray-400 truncate flex-1" title={job.id}>
                {job.id.slice(0, 12)}...
              </span>
              <span className="text-gray-600">{job.queue}</span>
              {job.customer_id && <span className="text-gray-600 truncate max-w-20" title={job.customer_id}>{job.customer_id.slice(0, 10)}</span>}
              <span className={`px-1 py-0.5 rounded text-[10px] ${
                job.status === "processing" ? "bg-blue-500/20 text-blue-400" :
                job.status === "queued" ? "bg-gray-500/20 text-gray-400" :
                "bg-red-500/20 text-red-400"
              }`}>{job.status}</span>
              <button
                onClick={() => handleClear("job", job.id)}
                className="text-gray-600 hover:text-red-400 p-0.5"
                title="Cancel job"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      ) : data ? (
        <div className="text-center py-4 text-gray-600 text-xs">Queue empty</div>
      ) : null}
    </div>
  )
}

function ServicesPanel() {
  const [services, setServices] = useState<ServiceInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [restarting, setRestarting] = useState<string | null>(null)
  const [message, setMessage] = useState("")

  useEffect(() => {
    setLoading(true)
    getServices().then(d => setServices(d.services)).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const handleRestart = async (name: string) => {
    // Map container names back to service keys
    const keyMap: Record<string, string> = {
      "cloakai-api": "api", "cloakai-worker": "worker", "cloakai-web": "web",
      "api": "api", "worker": "worker", "web": "web",
    }
    const key = keyMap[name]
    if (!key) { setMessage(`Unknown service: ${name}`); return }
    if (!confirm(`Restart ${name}? This will cause brief downtime.`)) return

    setRestarting(name)
    try {
      const result = await restartService(key)
      setMessage(result.success ? `Restarted ${name}` : `Failed: ${result.detail}`)
    } catch (e: any) {
      setMessage(`Error: ${e.message}`)
    }
    setRestarting(null)
    setTimeout(() => setMessage(""), 5000)
  }

  if (loading) return <div className="text-center py-4"><RefreshCw size={14} className="text-gray-600 animate-spin mx-auto" /></div>

  return (
    <div className="space-y-2">
      {message && <div className="text-xs text-accent">{message}</div>}

      {/* Quick restart buttons */}
      <div className="grid grid-cols-3 gap-2">
        {["api", "worker", "web"].map(svc => (
          <button
            key={svc}
            onClick={() => handleRestart(svc)}
            disabled={restarting === svc}
            className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-card border border-border text-xs text-gray-300 hover:bg-white/5 disabled:opacity-50"
          >
            {restarting === svc ? (
              <RefreshCw size={11} className="animate-spin" />
            ) : (
              <RotateCcw size={11} />
            )}
            {svc.charAt(0).toUpperCase() + svc.slice(1)}
          </button>
        ))}
      </div>

      {/* Service list from Docker/Railway */}
      {services.length > 0 && (
        <div className="space-y-1">
          {services.map((svc, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-card/30 border border-border/20 text-xs">
              <Server size={11} className="text-gray-500 shrink-0" />
              <span className="text-gray-300 flex-1 truncate">{svc.name}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                svc.status === "running" ? "bg-green-500/20 text-green-400" :
                svc.status === "exited" || svc.status === "CRASHED" ? "bg-red-500/20 text-red-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {svc.status}
              </span>
              <button
                onClick={() => handleRestart(svc.name)}
                disabled={!!restarting}
                className="text-gray-600 hover:text-accent p-0.5 disabled:opacity-50"
                title={`Restart ${svc.name}`}
              >
                <RotateCcw size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ApiTestPanel() {
  const [method, setMethod] = useState("GET")
  const [path, setPath] = useState("/health")
  const [body, setBody] = useState("")
  const [result, setResult] = useState<ApiTestResult | null>(null)
  const [testing, setTesting] = useState(false)

  const PRESETS = [
    { label: "Health", method: "GET", path: "/health" },
    { label: "Engine", method: "GET", path: "/health/engine" },
    { label: "Disk", method: "GET", path: "/health/disk" },
    { label: "System", method: "GET", path: "/health/system" },
    { label: "Metrics", method: "GET", path: "/metrics/json" },
    { label: "Queue", method: "GET", path: "/v1/queue/stats" },
  ]

  const handleTest = async () => {
    setTesting(true)
    try {
      let parsedBody = undefined
      if (body.trim() && method !== "GET") {
        try { parsedBody = JSON.parse(body) } catch { parsedBody = body }
      }
      const r = await testApi(method, path, parsedBody)
      setResult(r)
    } catch (e: any) {
      setResult({ status: 0, elapsed_ms: 0, headers: {}, body: null, error: e.message })
    }
    setTesting(false)
  }

  return (
    <div className="space-y-3">
      {/* Presets */}
      <div className="flex flex-wrap gap-1">
        {PRESETS.map(p => (
          <button
            key={p.path}
            onClick={() => { setMethod(p.method); setPath(p.path); setResult(null) }}
            className={`px-2 py-1 rounded text-[10px] border transition-colors ${
              path === p.path ? "bg-accent/20 border-accent/30 text-accent" : "bg-card/40 border-border/30 text-gray-500 hover:text-gray-300"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Request builder */}
      <div className="flex gap-1.5">
        <select
          value={method}
          onChange={e => setMethod(e.target.value)}
          className="bg-card border border-border rounded-md px-2 py-1.5 text-xs text-gray-300 focus:outline-none"
        >
          {["GET", "POST", "PUT", "DELETE"].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          value={path}
          onChange={e => setPath(e.target.value)}
          placeholder="/health"
          className="flex-1 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs text-gray-300 font-mono placeholder:text-gray-600 focus:outline-none focus:border-accent/50"
        />
        <button
          onClick={handleTest}
          disabled={testing || !path}
          className="px-3 py-1.5 rounded-md bg-accent/20 hover:bg-accent/30 text-accent text-xs border border-accent/20 disabled:opacity-50"
        >
          {testing ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />}
        </button>
      </div>

      {/* Body (for POST/PUT) */}
      {method !== "GET" && (
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder='{"key": "value"}'
          rows={2}
          className="w-full bg-card border border-border rounded-md px-2.5 py-1.5 text-xs text-gray-300 font-mono placeholder:text-gray-600 focus:outline-none focus:border-accent/50 resize-none"
        />
      )}

      {/* Result */}
      {result && (
        <div className="rounded-lg bg-card/50 border border-border/30 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/20">
            <span className={`font-mono text-xs font-bold ${
              result.error ? "text-red-400" :
              result.status < 300 ? "text-green-400" :
              result.status < 500 ? "text-amber-400" : "text-red-400"
            }`}>
              {result.error ? "ERR" : result.status}
            </span>
            {result.elapsed_ms > 0 && <span className="text-[10px] text-gray-600">{result.elapsed_ms}ms</span>}
          </div>
          <pre className="px-3 py-2 text-[11px] text-gray-400 font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
            {result.error || (typeof result.body === "string" ? result.body : JSON.stringify(result.body, null, 2))}
          </pre>
        </div>
      )}
    </div>
  )
}

// ── Center Column (original CommandScreen content) ───────────────────────

function CenterColumn() {
  // Use Zustand store for all data
  const snapshot = useSystemStore(s => s.snapshot)
  const feed = useSystemStore(s => s.feed)
  const metrics = useSystemStore(s => s.metrics)
  const autopilot = useSystemStore(s => s.autopilot)
  const authority = useSystemStore(s => s.authority)
  const slos = useSystemStore(s => s.slos)
  const diagnosis = useSystemStore(s => s.diagnosis)
  const report = useSystemStore(s => s.report)
  const diagnosing = useSystemStore(s => s.diagnosing)
  const generating = useSystemStore(s => s.generating)

  const runDiagnosis = useSystemStore(s => s.runDiagnosis)
  const runReport = useSystemStore(s => s.runReport)
  const doToggleAutopilot = useSystemStore(s => s.doToggleAutopilot)
  const doVeto = useSystemStore(s => s.doVeto)
  const doAckAlert = useSystemStore(s => s.doAckAlert)
  const doResolveAlert = useSystemStore(s => s.doResolveAlert)
  const dismissDiagnosis = useSystemStore(s => s.dismissDiagnosis)
  const dismissReport = useSystemStore(s => s.dismissReport)

  // Derived values
  const s = snapshot
  const hasAlerts = s && (s.alerts_critical > 0 || s.alerts_warn > 0)
  const isHealthy = s && !hasAlerts && s.worker_alive
  const statusColor = !s ? "bg-gray-500" : s.alerts_critical > 0 ? "bg-red-500" : hasAlerts ? "bg-amber-500" : "bg-emerald-500"
  const statusText = !s ? "Connecting..." : s.alerts_critical > 0 ? "Critical" : hasAlerts ? "Degraded" : "Healthy"

  // Sparkline data
  const extract = (key: keyof MetricsPoint) => metrics.map(p => p[key] as number | null)

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* ── Zone 1: The Pulse ─────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 space-y-3">
        {/* Status header */}
        <div className="flex items-center gap-2.5">
          <div className={`w-2.5 h-2.5 rounded-full ${statusColor} ${isHealthy ? "" : "animate-pulse"}`} />
          <span className="text-sm font-medium text-gray-200">{statusText}</span>
          <div className="flex-1" />
          {s && (
            <span className="text-[10px] text-gray-600 font-mono">
              {s.total_cloaks?.toLocaleString()} protected
            </span>
          )}
        </div>

        {/* 4 stat cards */}
        <div className="grid grid-cols-4 gap-2">
          <PulseCard
            value={s?.active_jobs ?? 0}
            label="Active"
            sub={s && s.queue_depth > 0 ? `${s.queue_depth} queued` : undefined}
            color={s && s.queue_depth > 20 ? "#f59e0b" : undefined}
          />
          <PulseCard
            value={s?.throughput_jobs_per_min ?? 0}
            label="Jobs/min"
            color="#22c55e"
          />
          <PulseCard
            value={s?.gpu_pct != null ? `${s.gpu_pct}%` : "—"}
            label="GPU"
            sub={s?.gpu_temp != null ? `${s.gpu_temp}°C` : undefined}
            color={s && (s.gpu_pct ?? 0) > 90 ? "#ef4444" : "#f59e0b"}
          />
          <PulseCard
            value={s?.gpu_workers_ready ?? 0}
            label="Workers"
            sub={s && s.gpu_workers_alive > 0 ? `/${s.gpu_workers_alive} alive` : undefined}
            color={s && s.gpu_workers_ready === 0 && s.gpu_workers_alive > 0 ? "#ef4444" : "#8b5cf6"}
          />
        </div>

        {/* SLO burn rates */}
        <SloRow slos={slos} />

        {/* Autopilot row */}
        <AutopilotRow
          autopilot={autopilot}
          authority={authority}
          onToggle={doToggleAutopilot}
          onVeto={doVeto}
        />

        {/* Pending veto actions */}
        {authority && authority.pending_actions.length > 0 && (
          <VetoPanel actions={authority.pending_actions} onVeto={doVeto} />
        )}

        {/* Alert banner (if critical) */}
        {s && s.alerts_critical > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertTriangle size={14} className="text-red-400 shrink-0" />
            <span className="text-xs text-red-300">
              {s.alerts_critical} critical alert{s.alerts_critical > 1 ? "s" : ""} — check feed below
            </span>
          </div>
        )}
      </div>

      {/* ── Zone 2: The Feed ──────────────────────────────────────── */}
      <Section title="Live Feed" icon={Activity} defaultOpen badge={`${feed.length}`}>
        {feed.length > 0 ? (
          <div className="divide-y divide-border/30">
            {feed.map((entry, i) => (
              <InteractiveFeedItem
                key={`${entry.ts}-${i}`}
                entry={entry}
                onAck={doAckAlert}
                onResolve={doResolveAlert}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 text-sm">
            <Activity size={20} className="mx-auto mb-2 text-gray-600" />
            No activity yet. Waiting for events...
          </div>
        )}
      </Section>

      {/* ── Zone 3: Vitals ────────────────────────────────────────── */}
      <Section title="Vitals" icon={TrendingUp} defaultOpen={false} badge="1h">
        <div className="grid grid-cols-2 gap-2">
          <MiniSparkline
            data={extract("gpu_util_pct")}
            color="#f59e0b"
            label="GPU"
            current={s?.gpu_pct ?? "—"}
            unit="%"
          />
          <MiniSparkline
            data={extract("queue_total")}
            color="#818cf8"
            label="Queue"
            current={s?.queue_depth ?? 0}
          />
          <MiniSparkline
            data={extract("jobs_per_min")}
            color="#22c55e"
            label="Throughput"
            current={s?.throughput_jobs_per_min ?? 0}
            unit="/min"
          />
          <MiniSparkline
            data={extract("active_clients")}
            color="#06b6d4"
            label="Users"
            current={metrics.length > 0 ? (metrics[metrics.length - 1].active_clients ?? 0) : 0}
          />
          <MiniSparkline
            data={extract("disk_pct")}
            color={s && (s.disk_pct ?? 0) > 85 ? "#ef4444" : "#8b5cf6"}
            label="Disk"
            current={s?.disk_pct != null ? Math.round(s.disk_pct) : "—"}
            unit="%"
          />
          <MiniSparkline
            data={extract("avg_proc_s")}
            color="#ec4899"
            label="Avg Job"
            current={s?.throughput_avg_s ?? "—"}
            unit="s"
          />
        </div>
      </Section>

      {/* ── Ops (Queue, Services, Runbooks, API Test) ─────────────── */}
      <Section title="Ops" icon={Terminal} defaultOpen={false} badge="controls">
        <OpsPanel />
      </Section>

      {/* ── Diagnosis result (if triggered) ───────────────────────── */}
      {diagnosis && (
        <Section title="Diagnosis" icon={Search} defaultOpen>
          <div className="space-y-3">
            <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
              {diagnosis.analysis}
            </div>
            {diagnosis.actions?.length > 0 && (
              <div className="space-y-1.5">
                {diagnosis.actions.map((action: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-400">
                    <Zap size={10} className="text-accent shrink-0 mt-0.5" />
                    <span>{action.label}</span>
                    {action.auto_fixable && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-accent/20 text-accent ml-auto shrink-0">auto</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={dismissDiagnosis}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Dismiss
            </button>
          </div>
        </Section>
      )}

      {/* ── Shift Report (if generated) ─────────────────────────── */}
      {report && (
        <Section title="Shift Report" icon={FileText} defaultOpen>
          <div className="space-y-3">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-card border border-border px-3 py-2 text-center">
                <div className="text-lg font-mono text-white">{report.jobs?.total ?? 0}</div>
                <div className="text-[10px] text-gray-500">Jobs</div>
              </div>
              <div className="rounded-lg bg-card border border-border px-3 py-2 text-center">
                <div className="text-lg font-mono text-white">{report.alerts?.total ?? 0}</div>
                <div className="text-[10px] text-gray-500">Alerts</div>
              </div>
              <div className="rounded-lg bg-card border border-border px-3 py-2 text-center">
                <div className="text-lg font-mono text-white">{report.autopilot_actions?.length ?? 0}</div>
                <div className="text-[10px] text-gray-500">Actions</div>
              </div>
            </div>
            {/* Period */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Clock size={10} />
              {report.duration_hours ? `${report.duration_hours}h window` : ""}
              {report.overall_status && (
                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
                  report.overall_status === "all_clear" ? "bg-green-500/20 text-green-400" :
                  report.overall_status === "needs_attention" ? "bg-red-500/20 text-red-400" :
                  "bg-amber-500/20 text-amber-400"
                }`}>
                  {report.overall_status.replace(/_/g, " ")}
                </span>
              )}
            </div>
            {/* LLM summary */}
            {report.summary && (
              <div className="prose prose-invert prose-sm max-w-none [&_p]:text-sm [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:my-1 [&_h3]:text-sm [&_h3]:text-gray-200 [&_h3]:mt-2 [&_h3]:mb-1 [&_strong]:text-gray-200 [&_ul]:text-sm [&_ul]:text-gray-300 [&_ul]:my-1 [&_li]:my-0.5">
                <Markdown>{report.summary}</Markdown>
              </div>
            )}
            <button
              onClick={dismissReport}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Dismiss
            </button>
          </div>
        </Section>
      )}

      {/* ── Zone 4: Quick Actions ─────────────────────────────────── */}
      <div className="sticky bottom-0 bg-[#0a0a14]/90 backdrop-blur-sm border-t border-border/40 px-4 py-3">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={runDiagnosis}
            disabled={diagnosing}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-medium transition-colors border border-border/40 disabled:opacity-50"
          >
            {diagnosing ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <Search size={13} />
            )}
            Diagnose
          </button>
          <button
            onClick={runReport}
            disabled={generating}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-medium transition-colors border border-border/40 disabled:opacity-50"
          >
            {generating ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <FileText size={13} />
            )}
            Report
          </button>
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent("pulse:open-chat"))
            }}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent text-xs font-medium transition-colors border border-accent/20"
          >
            <MessageCircle size={13} />
            Chat
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main screen (three-column layout) ────────────────────────────────────

export default function CommandScreen() {
  const loading = useSystemStore(s => s.loading)
  const loadAll = useSystemStore(s => s.loadAll)
  const loadSnapshot = useSystemStore(s => s.loadSnapshot)
  const loadFeed = useSystemStore(s => s.loadFeed)
  const loadMetrics = useSystemStore(s => s.loadMetrics)
  const loadAutopilot = useSystemStore(s => s.loadAutopilot)
  const loadSlos = useSystemStore(s => s.loadSlos)
  const selectedNodeId = useSystemStore(s => s.selectedNodeId)

  // Mobile: toggle topology visibility
  const [showTopo, setShowTopo] = useState(false)

  // Initial load + polling
  useEffect(() => {
    loadAll()

    const snapInterval = setInterval(loadSnapshot, 5_000)
    const feedInterval = setInterval(loadFeed, 15_000)
    const metricsInterval = setInterval(loadMetrics, 60_000)
    const autopilotInterval = setInterval(loadAutopilot, 10_000)
    const sloInterval = setInterval(loadSlos, 30_000)

    return () => {
      clearInterval(snapInterval)
      clearInterval(feedInterval)
      clearInterval(metricsInterval)
      clearInterval(autopilotInterval)
      clearInterval(sloInterval)
    }
  }, [loadAll, loadSnapshot, loadFeed, loadMetrics, loadAutopilot, loadSlos])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw size={20} className="text-gray-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left: Network Topology (40% on desktop, collapsible on mobile) ── */}
      <div className="hidden lg:flex flex-col border-r border-border/30" style={{ width: "40%" }}>
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30">
          <Map size={13} className="text-indigo-400" />
          <span className="text-[11px] text-gray-400 uppercase tracking-wider font-medium">System Topology</span>
        </div>
        <div className="flex-1">
          <NetworkTopology />
        </div>
      </div>

      {/* Mobile topology toggle */}
      <button
        onClick={() => setShowTopo(!showTopo)}
        className="lg:hidden fixed bottom-20 left-3 z-30 p-2.5 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 shadow-lg"
      >
        <Map size={16} />
      </button>

      {/* Mobile topology drawer */}
      {showTopo && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#0a0a14]/95 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
            <div className="flex items-center gap-2">
              <Map size={13} className="text-indigo-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">System Topology</span>
            </div>
            <button onClick={() => setShowTopo(false)} className="p-1 text-gray-500 hover:text-gray-300">
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 relative">
            <NetworkTopology />
            {/* Mobile node detail bottom sheet */}
            {selectedNodeId && (
              <div className="absolute bottom-0 left-0 right-0 max-h-[50%] bg-surface border-t border-border/40 rounded-t-2xl shadow-2xl overflow-y-auto animate-in slide-in-from-bottom-4">
                <div className="w-8 h-1 rounded-full bg-gray-700 mx-auto mt-2" />
                <NodeDetailDrawer />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Center: Status / Feed / Vitals / Ops (35% on desktop, full on mobile) ── */}
      <div className="flex-1 min-w-0 lg:max-w-none" style={{ flexBasis: "35%" }}>
        <CenterColumn />
      </div>

      {/* ── Right: AI Diagnosis + Node Details (25% on desktop, drawer on mobile) ── */}
      <div className="hidden lg:flex flex-col border-l border-border/30" style={{ width: "25%" }}>
        {selectedNodeId ? (
          <NodeDetailDrawer />
        ) : (
          <DiagnosisPanel />
        )}
      </div>
    </div>
  )
}
